/**
 * How a running plan and its crew are told apart, on both sides (D178,
 * docs/plans/2026-10-concurrent-plans.md): the host keys crew sessions and
 * stamps plans with a colour; the renderer draws that colour and the member's
 * plain name. One definition so the board, the timeline, the office, the team
 * map and Mission Control never disagree.
 *
 * Pure: no Node, no DOM. MUST NOT import `vscode`.
 */
/** How many plan colours there are; the renderer defines `--plan-tone-0` … `--plan-tone-7`. */
export const PLAN_COLOR_COUNT = 8;
/**
 * A plan's colour slot: the one stored on the plan when it was approved
 * (`Plan.colorIndex`, picked by {@link pickPlanColor} so running plans differ),
 * else a stable hash of its id.
 */
export function planColorIndex(plan) {
    if (typeof plan.colorIndex === "number" && Number.isInteger(plan.colorIndex) && plan.colorIndex >= 0)
        return plan.colorIndex % PLAN_COLOR_COUNT;
    let h = 0;
    for (const ch of plan.id)
        h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return h % PLAN_COLOR_COUNT;
}
/** The first colour no running plan uses (wrapping when all are taken), for a newly approved plan. */
export function pickPlanColor(running) {
    const used = new Set(running.map((p) => planColorIndex(p)));
    for (let i = 0; i < PLAN_COLOR_COUNT; i++)
        if (!used.has(i))
            return i;
    return running.length % PLAN_COLOR_COUNT;
}
/** Separates a member's name from its plan in a crew session's internal key. */
export const CREW_KEY_SEPARATOR = "·";
/** The internal, unique name of a crew member's session: `Mira·PLAN-2`. Never shown to people. */
export function crewMemberKey(member, planRef) {
    return `${member}${CREW_KEY_SEPARATOR}${planRef}`;
}
/** The member and plan a crew key names, or undefined for an ordinary member name. */
export function parseCrewKey(key) {
    const i = key.lastIndexOf(CREW_KEY_SEPARATOR);
    if (i <= 0 || i === key.length - 1)
        return undefined;
    return { member: key.slice(0, i), planRef: key.slice(i + CREW_KEY_SEPARATOR.length) };
}
/** The name people see for a session: the member it is a copy of, never the crew key. */
export function displayMemberName(session) {
    return session.crew?.member ?? parseCrewKey(session.identity.name)?.member ?? session.identity.name;
}
//# sourceMappingURL=planIdentity.js.map